/**
 * PresenterConsole — the director's control panel for the Worship
 * Presentation Engine. Broadcasts state (current slide, blackout, freeze,
 * theme override) to any open output windows via BroadcastChannel.
 *
 * Route: /worship/present/:serviceId
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Monitor, Presentation,
  Eye, EyeOff, Snowflake, PlayCircle, Palette, Music2, Captions, RadioTower, Scissors, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getToken } from '@/lib/authToken';
import { usePresenterChannel } from './usePresenterChannel';
import SlideCanvas from './SlideCanvas';
import { PRESET_BACKGROUNDS, DEFAULT_THEME } from './backgrounds';
import VolunteerAssignDialog, { NotifyTeamButton } from '@/pages/worship/VolunteerAssignDialog';
import useCaptionEngine from './useCaptionEngine';
import ObsSceneDrawer from './ObsSceneDrawer';
import TranslationPicker from './TranslationPicker';
import SermonClipperDrawer from './SermonClipperDrawer';
import ProgramPreview from './ProgramPreview';
import LeadSheetViewer from './LeadSheetViewer';
import { MediaUpload } from '@/components/MediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PresenterConsole() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [slideIdx, setSlideIdx] = useState(0);
  const [blackout, setBlackout] = useState(false);
  const [freeze, setFreeze] = useState(false);
  const [themeDraft, setThemeDraft] = useState(null); // unsaved theme (broadcast only)
  const [showTheme, setShowTheme] = useState(false);
  const [chordMode, setChordMode] = useState(false);
  const [transpose, setTranspose] = useState(0);
  const [assignBlockIdx, setAssignBlockIdx] = useState(null);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionOutputs, setCaptionOutputs] = useState({ audience: true, stage: true, confidence: true });
  const [captionsBuffer, setCaptionsBuffer] = useState([]);   // { text, at }
  const [captionLanguages, setCaptionLanguages] = useState([]);   // ['es', 'pt', ...]
  const [showObs, setShowObs] = useState(false);
  const [showClipper, setShowClipper] = useState(false);
  const [showLeadSheet, setShowLeadSheet] = useState(false);
  const openedRef = useRef({});

  const payloadQ = useQuery({
    queryKey: ['worship-present', serviceId],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/worship/services/${serviceId}/present`,
      { headers: authH() },
    )).data,
  });

  const slides = payloadQ.data?.slides || [];
  const baseTheme = payloadQ.data?.theme || DEFAULT_THEME;
  const theme = themeDraft || baseTheme;
  const service = payloadQ.data?.service;

  // Hydrate persisted target languages once the service loads
  useEffect(() => {
    if (service?.caption_languages) setCaptionLanguages(service.caption_languages);
  }, [service?.id, service?.caption_languages]);

  const persistLanguages = useMutation({
    mutationFn: async (langs) => (await axios.put(
      `${API}/api/admin/worship/services/${serviceId}`,
      { caption_languages: langs },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onError: () => { /* fire-and-forget */ },
  });

  // Broadcast state to any open output windows
  const stateMsg = useMemo(() => ({
    kind: 'state', slideIdx, blackout, freeze, theme, chordMode, transpose,
    captionsEnabled, captionOutputs, captionLanguages,
  }), [slideIdx, blackout, freeze, theme, chordMode, transpose, captionsEnabled, captionOutputs, captionLanguages]);

  const { post } = usePresenterChannel(serviceId, {
    role: 'director',
    onMessage: (msg) => {
      // Outputs ping us for the latest state when they load
      if (msg && msg.kind === 'sync?') {
        post(stateMsg);
        // Replay last few captions
        captionsBuffer.slice(-3).forEach((c) => post({ kind: 'caption', text: c.text, at: c.at }));
      }
    },
  });

  useEffect(() => { post(stateMsg); }, [post, stateMsg]);

  // Caption engine — final segments both go into local buffer AND broadcast
  const captions = useCaptionEngine({
    onFinal: async (text) => {
      const at = Date.now();
      const line = { text, at, translations: {} };
      setCaptionsBuffer((prev) => {
        const next = [...prev, line];
        return next.length > 30 ? next.slice(-30) : next;
      });
      post({ kind: 'caption', text, at });
      // Translate in parallel if any target languages are set
      if (captionLanguages.length > 0) {
        try {
          const r = await axios.post(
            `${API}/api/admin/worship/captions/translate`,
            { text, src: 'en', targets: captionLanguages },
            { headers: { ...authH(), 'Content-Type': 'application/json' } },
          );
          const translations = r.data?.translations || {};
          if (Object.keys(translations).length) {
            post({ kind: 'caption-translations', at, translations });
            setCaptionsBuffer((prev) => prev.map(
              (c) => (c.at === at ? { ...c, translations } : c),
            ));
          }
        } catch (e) { /* swallow — original caption already delivered */ }
      }
    },
  });

  // Start/stop engine when the toggle flips
  useEffect(() => {
    if (captionsEnabled && !captions.isListening) captions.start();
    if (!captionsEnabled && captions.isListening) captions.stop();
  }, [captionsEnabled, captions]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        setSlideIdx((i) => Math.min(i + 1, slides.length - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setSlideIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Home') {
        setSlideIdx(0);
      } else if (e.key === 'End') {
        setSlideIdx(Math.max(0, slides.length - 1));
      } else if (e.key === 'b' || e.key === 'B') {
        setBlackout((v) => !v);
      } else if (e.key === 'f' || e.key === 'F') {
        setFreeze((v) => !v);
      } else if (e.key === 'Escape') {
        setBlackout(false); setFreeze(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [slides.length]);

  const openOutput = useCallback((mode) => {
    const url = `/worship/output/${serviceId}/${mode}`;
    const features = 'noopener=no,menubar=no,toolbar=no,location=no,status=no,resizable=yes,width=1280,height=720';
    const w = window.open(url, `worship-out-${serviceId}-${mode}`, features);
    openedRef.current[mode] = w;
    if (!w) toast.error('Popup blocked — allow popups for this site to open the output window.');
  }, [serviceId]);

  const saveTheme = useMutation({
    mutationFn: async () => (await axios.put(
      `${API}/api/admin/worship/services/${serviceId}/theme`,
      themeDraft || theme,
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Theme saved');
      setThemeDraft(null);
      qc.invalidateQueries({ queryKey: ['worship-present', serviceId] });
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  // Effective OBS config (service override → church default → ecosystem)
  const obsEffectiveQ = useQuery({
    queryKey: ['obs-effective', serviceId],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/worship/services/${serviceId}/obs-effective`, { headers: authH() },
    )).data,
  });

  // Fire OBS scene switch whenever the current slide's block_type changes
  const lastFiredRef = useRef({ blockType: null, connectionId: null });
  useEffect(() => {
    const cfg = obsEffectiveQ.data?.config;
    if (!cfg?.connection_id || !cfg?.auto_switch) return;
    const current = payloadQ.data?.slides?.[slideIdx];
    if (!current) return;
    const scene = cfg.scene_map?.[current.type];
    if (!scene) return;
    const sig = `${cfg.connection_id}::${current.block_index}::${scene}`;
    if (lastFiredRef.current.sig === sig) return;
    lastFiredRef.current.sig = sig;
    axios.post(
      `${API}/api/obs/connections/${cfg.connection_id}/scene`,
      { scene_name: scene },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    ).catch(() => { /* swallow — OBS may be offline; user sees no toast noise */ });
  }, [slideIdx, obsEffectiveQ.data, payloadQ.data]);

  if (payloadQ.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center" data-testid="console-loading">
        Loading service…
      </div>
    );
  }
  if (payloadQ.isError) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center" data-testid="console-error">
        Failed to load service.
      </div>
    );
  }

  const current = slides[slideIdx];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col" data-testid="presenter-console">
      {/* Top bar */}
      <div className="flex items-center gap-2 border-b border-slate-800 bg-slate-950/95 px-3 py-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/worship')} data-testid="console-back">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-100">
            {service?.name || 'Service'}
          </div>
          <div className="text-[10px] text-slate-500">
            {slides.length} slides · {service?.block_count || 0} blocks
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant={blackout ? 'default' : 'outline'} data-testid="btn-blackout"
                  className={blackout ? 'bg-rose-600 hover:bg-rose-500' : 'border-slate-700 text-slate-200 hover:bg-slate-800'}
                  onClick={() => setBlackout((v) => !v)}>
            {blackout ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            <span className="ml-1 hidden sm:inline">Blackout</span>
          </Button>
          <Button size="sm" variant={freeze ? 'default' : 'outline'} data-testid="btn-freeze"
                  className={freeze ? 'bg-sky-600 hover:bg-sky-500' : 'border-slate-700 text-slate-200 hover:bg-slate-800'}
                  onClick={() => setFreeze((v) => !v)}>
            <Snowflake className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Freeze</span>
          </Button>
          <Button size="sm" variant="outline" data-testid="btn-theme" onClick={() => setShowTheme((v) => !v)}
                  className="border-slate-700 text-slate-200 hover:bg-slate-800">
            <Palette className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Theme</span>
          </Button>
        </div>
      </div>

      {/* Output launcher row */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 bg-slate-950 px-3 py-2 text-xs">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Outputs</span>
        <Button size="sm" variant="outline" data-testid="open-audience"
                className="border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20"
                onClick={() => openOutput('audience')}>
          <Presentation className="mr-1 h-3 w-3" /> Projection
        </Button>
        <Button size="sm" variant="outline" data-testid="open-stage"
                className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                onClick={() => openOutput('stage')}>
          <Monitor className="mr-1 h-3 w-3" /> Stage Display
        </Button>
        <Button size="sm" variant="outline" data-testid="open-confidence"
                className="border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                onClick={() => openOutput('confidence')}>
          <PlayCircle className="mr-1 h-3 w-3" /> Confidence Monitor
        </Button>
        <span className="ml-auto text-[10px] text-slate-500">
          Popups sync via BroadcastChannel · shortcut keys: ← → space · B blackout · F freeze
        </span>
      </div>

      {/* Musician row — chord mode + transpose + captions + OBS */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 bg-slate-950/70 px-3 py-2 text-xs">
        <button
          type="button"
          data-testid="btn-chord-mode"
          onClick={() => setChordMode((v) => !v)}
          className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] ${
            chordMode
              ? 'border-amber-500 bg-amber-500/20 text-amber-200'
              : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600'
          }`}
        >
          <Music2 className="h-3 w-3" /> Chord Mode {chordMode ? 'on' : 'off'}
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Transpose</span>
          <button type="button" data-testid="transpose-down" onClick={() => setTranspose((v) => Math.max(v - 1, -11))}
                  className="rounded border border-slate-700 bg-slate-950 px-2 py-0.5 text-slate-300 hover:border-slate-500">−</button>
          <input
            data-testid="transpose-input"
            type="number"
            min={-11}
            max={11}
            value={transpose}
            onChange={(e) => setTranspose(Math.max(-11, Math.min(11, Number(e.target.value) || 0)))}
            className="h-6 w-14 rounded border border-slate-700 bg-slate-900 text-center text-slate-100"
          />
          <button type="button" data-testid="transpose-up" onClick={() => setTranspose((v) => Math.min(v + 1, 11))}
                  className="rounded border border-slate-700 bg-slate-950 px-2 py-0.5 text-slate-300 hover:border-slate-500">+</button>
          <button type="button" data-testid="transpose-reset" onClick={() => setTranspose(0)}
                  className="text-slate-500 hover:text-slate-300">reset</button>
          {transpose !== 0 && (
            <span className="text-amber-300">
              {transpose > 0 ? '+' : ''}{transpose} semitones
            </span>
          )}
        </div>

        {/* Captions toolbar */}
        <div className="flex items-center gap-2 rounded-full border border-slate-800 bg-slate-950 px-2 py-1">
          <button
            type="button"
            data-testid="btn-captions"
            onClick={() => setCaptionsEnabled((v) => !v)}
            className={`flex items-center gap-1 text-[10px] ${
              captionsEnabled ? 'text-emerald-300' : 'text-slate-400 hover:text-slate-200'
            }`}
            title={captions.error || (captions.supported ? 'Toggle live captions' : 'Engine not supported in this browser')}
          >
            <Captions className="h-3 w-3" /> Captions {captionsEnabled ? 'on' : 'off'}
          </button>
          <select
            data-testid="captions-engine"
            value={captions.engine}
            onChange={(e) => captions.setEngine(e.target.value)}
            className="h-6 rounded border border-slate-700 bg-slate-950 px-1 text-[10px] text-slate-300"
          >
            <option value="webspeech">Web Speech</option>
            <option value="whisper">Whisper</option>
          </select>
          <span className="flex items-center gap-1 text-[10px] text-slate-500">
            {['audience', 'stage', 'confidence'].map((o) => (
              <label key={o} className="flex items-center gap-0.5">
                <input
                  type="checkbox"
                  data-testid={`captions-output-${o}`}
                  checked={captionOutputs[o]}
                  onChange={(e) => setCaptionOutputs((prev) => ({ ...prev, [o]: e.target.checked }))}
                  className="h-3 w-3"
                />
                {o[0].toUpperCase()}
              </label>
            ))}
          </span>
          {captions.error && (
            <span className="text-[10px] text-rose-400" title={captions.error}>!</span>
          )}
        </div>

        {/* Translation picker */}
        <TranslationPicker
          value={captionLanguages}
          onChange={(next) => {
            setCaptionLanguages(next);
            persistLanguages.mutate(next);
          }}
        />

        {/* Sermon Clipper drawer trigger */}
        <button
          type="button"
          data-testid="btn-clipper"
          onClick={() => setShowClipper((v) => !v)}
          className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] ${
            showClipper
              ? 'border-fuchsia-500 bg-fuchsia-500/20 text-fuchsia-200'
              : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600'
          }`}
          title="Record and clip sermon audio"
        >
          <Scissors className="h-3 w-3" /> Clipper
        </button>

        {/* OBS drawer trigger */}
        <button
          type="button"
          data-testid="btn-obs"
          onClick={() => setShowObs((v) => !v)}
          className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] ${
            obsEffectiveQ.data?.config?.connection_id
              ? 'border-rose-500 bg-rose-500/20 text-rose-200'
              : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600'
          }`}
          title="Map worship blocks to OBS scenes"
        >
          <RadioTower className="h-3 w-3" /> OBS
          {obsEffectiveQ.data?.config?.connection_id && (
            <span className="ml-1 text-[9px] opacity-70">
              {Object.keys(obsEffectiveQ.data.config.scene_map || {}).length} mapped
            </span>
          )}
        </button>

        <span className="ml-auto flex items-center gap-2 text-[10px] text-slate-500">
          Musician view — audience never sees chords or transpose
        </span>
      </div>

      {/* Main layout: slide list · preview · next preview */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 p-2 md:grid-cols-[240px_1fr_260px]">
        {/* Slide list */}
        <div className="min-h-0 overflow-y-auto rounded border border-slate-800 bg-slate-900/40" data-testid="slide-list">
          {slides.map((s, i) => (
            <button
              key={s.index}
              type="button"
              data-testid={`slide-thumb-${i}`}
              onClick={() => setSlideIdx(i)}
              className={`block w-full border-b border-slate-800 px-2 py-2 text-left text-xs hover:bg-slate-800/60 ${
                i === slideIdx ? 'bg-violet-500/15' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="w-6 shrink-0 text-slate-500">{i + 1}</span>
                <Badge variant="outline" className="border-slate-700 text-[9px] text-slate-400">{s.type}</Badge>
                <span className="truncate text-slate-200">{s.title || s.type}</span>
              </div>
              {s.section_total > 1 && (
                <div className="ml-8 text-[9px] text-slate-500">
                  {s.section_label ? `${s.section_label} · ` : ''}
                  section {s.section_index + 1}/{s.section_total}
                </div>
              )}
            </button>
          ))}
          {slides.length === 0 && (
            <p className="p-3 text-xs text-slate-500">No slides — add blocks in the service planner.</p>
          )}
        </div>

        {/* Preview */}
        <div className="flex min-h-0 flex-col gap-2">
          <div className="relative min-h-0 flex-1 overflow-hidden rounded border border-slate-800">
            {blackout ? (
              <div className="flex h-full w-full items-center justify-center bg-black text-slate-600" data-testid="preview-blackout">
                <span className="text-xs uppercase tracking-widest">Blackout</span>
              </div>
            ) : (
              <SlideCanvas
                slide={current}
                theme={theme}
                variant={chordMode ? 'stage' : 'audience'}
                chordMode={chordMode}
                transposeSemitones={transpose}
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-2 rounded border border-slate-800 bg-slate-900/60 px-2 py-2">
            <Button size="sm" variant="outline" data-testid="prev-slide"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800"
                    onClick={() => setSlideIdx((i) => Math.max(i - 1, 0))} disabled={slideIdx === 0}>
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <div className="text-xs text-slate-400 tabular-nums" data-testid="slide-counter">
              {slides.length ? `${slideIdx + 1} / ${slides.length}` : '0 / 0'}
              {freeze && <span className="ml-2 text-sky-300">· frozen</span>}
              {blackout && <span className="ml-2 text-rose-300">· blackout</span>}
            </div>
            <Button size="sm" data-testid="next-slide"
                    className="bg-violet-600 hover:bg-violet-500"
                    onClick={() => setSlideIdx((i) => Math.min(i + 1, slides.length - 1))}
                    disabled={slideIdx >= slides.length - 1}>
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Next preview + notes */}
        <div className="flex min-h-0 flex-col gap-2">
          <div className="rounded border border-slate-800 bg-slate-900/60 p-2 text-xs">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-emerald-300">Up Next</div>
            {slides[slideIdx + 1] ? (
              <div className="aspect-video overflow-hidden rounded border border-slate-800">
                <SlideCanvas slide={slides[slideIdx + 1]} theme={theme} variant="audience" />
              </div>
            ) : (
              <div className="text-slate-500 py-6 text-center">End of service</div>
            )}
          </div>
          <div className="rounded border border-slate-800 bg-slate-900/60 p-2 text-xs">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300">Team on this block</div>
              {typeof current?.block_index === 'number' && (
                <button
                  type="button"
                  data-testid={`assign-btn-${current.block_index}`}
                  onClick={() => setAssignBlockIdx(current.block_index)}
                  className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-500/20"
                >
                  Manage
                </button>
              )}
            </div>
            <AssignmentSummary block={service?.blocks?.[current?.block_index]} />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[10px] text-slate-500">Notify all volunteers</span>
              <NotifyTeamButton serviceId={serviceId} />
            </div>
          </div>
          <div className="rounded border border-slate-800 bg-slate-900/60 p-2 text-xs">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-amber-300">Speaker Notes</div>
            <pre className="whitespace-pre-wrap text-slate-300">{current?.notes || '—'}</pre>
          </div>
          {current?.song?.lead_sheet_url && (
            <button
              type="button"
              data-testid={`open-lead-sheet-${current.song.id}`}
              onClick={() => setShowLeadSheet(true)}
              className="flex items-center justify-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 py-1.5 text-[11px] text-amber-200 hover:bg-amber-500/20"
            >
              <FileText className="h-3 w-3" /> Open Lead Sheet
            </button>
          )}
        </div>
      </div>

      <ProgramPreview connectionId={obsEffectiveQ.data?.config?.connection_id} />

      {showLeadSheet && current?.song?.lead_sheet_url && (
        <LeadSheetViewer
          songId={current.song.id}
          songTitle={current.song.title}
          url={current.song.lead_sheet_url}
          onClose={() => setShowLeadSheet(false)}
        />
      )}

      {showTheme && (
        <ThemeDrawer
          value={theme}
          baseTheme={baseTheme}
          onChange={(t) => setThemeDraft(t)}
          onReset={() => setThemeDraft(null)}
          onSave={() => saveTheme.mutate()}
          onClose={() => setShowTheme(false)}
          saving={saveTheme.isPending}
          dirty={!!themeDraft}
        />
      )}

      {assignBlockIdx !== null && service?.blocks?.[assignBlockIdx] && (
        <VolunteerAssignDialog
          serviceId={serviceId}
          blockIndex={assignBlockIdx}
          blockTitle={service.blocks[assignBlockIdx].title || service.blocks[assignBlockIdx].type}
          initialAssignments={service.blocks[assignBlockIdx].assignments || []}
          onClose={() => setAssignBlockIdx(null)}
        />
      )}

      {showObs && (
        <ObsSceneDrawer
          serviceId={serviceId}
          churchId={service?.church_id}
          onClose={() => setShowObs(false)}
        />
      )}

      {showClipper && (
        <SermonClipperDrawer
          serviceId={serviceId}
          isSermonActive={current?.type === 'sermon'}
          onClose={() => setShowClipper(false)}
        />
      )}

      {/* Captions live-preview strip (director-only, always shown when engine is running) */}
      {captionsEnabled && (
        <div className="fixed bottom-2 left-1/2 z-30 -translate-x-1/2 max-w-3xl rounded-lg border border-emerald-500/40 bg-black/80 px-3 py-1.5 text-center text-sm text-white shadow-xl" data-testid="captions-preview">
          {captions.current && <span className="opacity-70">{captions.current}</span>}
          {!captions.current && captionsBuffer.length === 0 && (
            <span className="text-slate-400 text-xs">Listening…</span>
          )}
          {!captions.current && captionsBuffer.length > 0 && (
            <span>{captionsBuffer[captionsBuffer.length - 1].text}</span>
          )}
        </div>
      )}
    </div>
  );
}


function AssignmentSummary({ block }) {
  const assignments = block?.assignments || [];
  if (assignments.length === 0) {
    return <p className="text-slate-500 text-[10px]">No one assigned yet.</p>;
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid="assignment-summary">
      {assignments.map((a, i) => (
        <span key={i} className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-100">
          <span className="opacity-70">{a.role}:</span> {a.person_name || a.person_email}
        </span>
      ))}
    </div>
  );
}


function ThemeDrawer({ value, baseTheme, onChange, onReset, onSave, onClose, saving, dirty }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-800 bg-slate-950/95 p-3 shadow-2xl" data-testid="theme-drawer">
      <div className="mx-auto max-w-5xl space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-100">Presentation Theme</div>
            <div className="text-[10px] text-slate-500">Changes broadcast live to open outputs. Save to persist.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            {dirty && (
              <Button size="sm" variant="outline" onClick={onReset} data-testid="theme-reset"
                      className="border-slate-700 text-slate-200 hover:bg-slate-800">
                Reset
              </Button>
            )}
            <Button size="sm" onClick={onSave} disabled={saving} data-testid="theme-save"
                    className="bg-violet-600 hover:bg-violet-500">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Preset</div>
          <div className="flex flex-wrap gap-2">
            {PRESET_BACKGROUNDS.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`theme-preset-${p.key}`}
                onClick={() => set({
                  preset: p.key, bg_type: p.bg_type, bg_value: p.bg_value,
                  accent_color: p.accent_color, text_color: p.text_color,
                })}
                className={`h-14 w-24 overflow-hidden rounded border-2 text-[10px] font-medium ${
                  value.preset === p.key ? 'border-violet-400' : 'border-slate-800'
                }`}
                style={{
                  background: p.bg_value,
                  color: p.text_color || '#f8fafc',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Background Type</label>
            <select data-testid="theme-bgtype" value={value.bg_type} onChange={(e) => set({ bg_type: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs">
              <option value="gradient">Gradient</option>
              <option value="color">Color</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Value</label>
            {value.bg_type === 'image' || value.bg_type === 'video' ? (
              <MediaUpload value={value.bg_value || ''} onChange={(url) => set({ bg_value: url })}
                           purpose={value.bg_type === 'video' ? 'video' : 'photo'}
                           testId="theme-bgvalue-upload" previewClassName="h-16" className="mt-1" />
            ) : (
              <input data-testid="theme-bgvalue" value={value.bg_value || ''} onChange={(e) => set({ bg_value: e.target.value })}
                      placeholder="#000 or linear-gradient(...)"
                      className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs" />
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Font</label>
            <select data-testid="theme-font" value={value.font_family} onChange={(e) => set({ font_family: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs">
              <option value="serif">Serif</option>
              <option value="sans">Sans</option>
              <option value="display">Display</option>
              <option value="mono">Mono</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Text</label>
            <input type="color" data-testid="theme-textcolor" value={value.text_color || '#f8fafc'} onChange={(e) => set({ text_color: e.target.value })}
                    className="mt-1 h-8 w-full rounded border border-slate-700 bg-slate-900" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Accent</label>
            <input type="color" data-testid="theme-accent" value={value.accent_color || '#a78bfa'} onChange={(e) => set({ accent_color: e.target.value })}
                    className="mt-1 h-8 w-full rounded border border-slate-700 bg-slate-900" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Align</label>
            <select data-testid="theme-align" value={value.text_align} onChange={(e) => set({ text_align: e.target.value })}
                    className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs">
              <option value="center">Center</option>
              <option value="left">Left</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Dim overlay</label>
            <input type="range" data-testid="theme-overlay" min="0" max="1" step="0.05"
                    value={value.overlay ?? 0.25}
                    onChange={(e) => set({ overlay: Number(e.target.value) })}
                    className="mt-2 w-full" />
            <div className="text-[10px] text-slate-500">{Math.round((value.overlay ?? 0.25) * 100)}%</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Blur (media)</label>
            <input type="range" data-testid="theme-blur" min="0" max="40" step="1"
                    value={value.blur ?? 0}
                    onChange={(e) => set({ blur: Number(e.target.value) })}
                    className="mt-2 w-full" />
            <div className="text-[10px] text-slate-500">{value.blur ?? 0}px</div>
          </div>
        </div>
      </div>
    </div>
  );
}
